import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

// LE-020 (S4-b): UI de impulsionamento — planos, compra de boost (POST
// /api/boosts/businesses/:id) e extrato de créditos (GET /credits).
// A carteira vive no comércio (businesses.credits_balance); o backend valida
// propriedade (owner_id), plano ativo, boost duplicado e saldo em transação.

const PLAN_GRADIENT = ['linear-gradient(135deg,#0d5c3b,#094a2f)', 'linear-gradient(135deg,#0e7490,#155e75)', 'linear-gradient(135deg,#7c3aed,#5b21b6)'];

const TYPE_LABEL = {
  bonus: { label: 'Bônus', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  spend: { label: 'Boost', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  purchase: { label: 'Compra', color: '#1d4ed8', bg: '#eff6ff' },
  refund: { label: 'Reembolso', color: 'var(--nc-text-secondary)', bg: 'var(--nc-surface-alt)' },
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

function remainingHours(iso) {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.max(1, Math.round(diff / 3600000));
}

function FeatureChip({ feature }) {
  const labels = {
    avatar_pulse: 'Pulso no avatar',
    banner: 'Banner em destaque',
    category_highlight: 'Destaque na categoria',
    wider_reach: 'Maior alcance',
    max_priority: 'Prioridade máxima',
  };
  const label = labels[feature] || feature;
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 600, color: 'var(--nc-primary)',
      background: '#ecfdf5', border: '1px solid var(--nc-success-border)', padding: '2px 8px', borderRadius: 10, margin: '2px 2px 0 0',
    }}>
      ✓ {label}
    </span>
  );
}

export function BoostPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from') || (isAdmin ? '/admin' : '/comerciante/estabelecimentos');

  const [businesses, setBusinesses] = useState([]);
  const [selected, setSelected] = useState(businessId || '');
  const [plans, setPlans] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [buying, setBuying] = useState(null); // id do plano em confirmação
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bizData, planData] = await Promise.all([
        api.get(isAdmin ? '/admin/businesses' : '/merchant/businesses', isAdmin ? { params: { status: 'approved', limit: 50 } } : undefined),
        api.get('/boosts/plans'),
      ]);
      const list = bizData.data.data || [];
      // Comerciante: só comércios ativos e aprovados podem ser impulsionados
      // (o backend exige is_approved nas rotas de boost — um pendente daria
      // 403 com mensagem enganosa). O admin já recebe a lista filtrada.
      const eligible = isAdmin ? list : list.filter((b) => b.is_active && b.is_approved);
      setBusinesses(eligible);
      setPlans(planData.data.data || []);
      if (!selected && eligible.length > 0) setSelected(String(eligible[0].id));
      return eligible;
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao carregar impulsionamento');
      return [];
    } finally {
      setLoading(false);
    }
  }, [selected, isAdmin]);

  const fetchDetails = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [boostData, creditData] = await Promise.all([
        api.get(`/boosts/businesses/${id}`),
        api.get(`/boosts/businesses/${id}/credits`),
      ]);
      setBoosts(boostData.data.data || []);
      setCredits(creditData.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao carregar dados do comércio');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]); // eslint-disable-line
  useEffect(() => { if (selected) fetchDetails(selected); }, [selected, fetchDetails]);

  const business = businesses.find((b) => String(b.id) === String(selected));
  const activeBoost = boosts.find((b) => b.is_active && !b.is_expired);

  const handleBuy = async (plan) => {
    if (!selected) {
      setMessage({ type: 'err', text: 'Selecione um estabelecimento antes de impulsionar.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.post(`/boosts/businesses/${selected}`, { plan_id: plan.id });
      setMessage({
        type: 'ok',
        text: `Impulsionamento ${plan.name} ativado! Restam ${data.remaining_credits} créditos.`,
      });
      setBuying(null);
      fetchDetails(selected);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      const msg = err.response?.data?.error?.message;
      if (code === 'BOOST_ALREADY_ACTIVE') {
        setMessage({ type: 'err', text: 'Este comércio já possui um impulsionamento ativo.' });
      } else if (code === 'INSUFFICIENT_CREDITS') {
        setMessage({ type: 'err', text: `${msg} Peça créditos à equipe Nossa Cidade.` });
      } else if (code === 'FORBIDDEN') {
        setMessage({ type: 'err', text: 'Você não administra este estabelecimento.' });
      } else {
        setMessage({ type: 'err', text: msg || 'Erro ao ativar o impulsionamento.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const changeBusiness = (e) => {
    const id = e.target.value;
    setSelected(id);
    setMessage(null);
    setBuying(null);
    navigate(`/comerciante/impulsionar/${id}`, { replace: true });
  };

  const inputSelectStyle = {
    width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, background: 'var(--nc-surface)', color: 'var(--nc-text)', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div className="nc-page">
      <div style={{
        background: 'linear-gradient(135deg, var(--nc-primary) 0%, var(--nc-primary-dark) 100%)',
        color: 'var(--nc-on-primary)', padding: '18px 16px 22px', borderRadius: '0 0 22px 22px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button onClick={() => navigate(from)} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div style={{ flex: 1 }}>
            <h1 className="nc-page-title">Impulsionar comércio</h1>
            <p style={{ margin: '2px 0 0', fontSize: 12, opacity: 0.85 }}>
              Apareça primeiro para a sua cidade
            </p>
          </div>
        </div>
        {businesses.length > 1 && (
          <select value={selected} onChange={changeBusiness} aria-label="Selecionar comércio" style={{
            ...inputSelectStyle, marginTop: 4,
          }}>
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {loading ? <Loading message="Carregando impulsionamento..." /> : error ? (
          <div role="alert" style={{
            padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13, marginBottom: 12,
          }}>
            {error}
            <button className="nc-btn-primary" style={{ marginTop: 10, width: '100%' }} onClick={() => (selected ? fetchDetails(selected) : fetchAll())}>
              Tentar novamente
            </button>
          </div>
        ) : !business ? (
          <EmptyState icon="🚀" title="Nenhum comércio disponível" description="Cadastre e aguarde a aprovação do seu estabelecimento para impulsionar." />
        ) : (
          <>
            {message && (
              <div role="status" style={{
                padding: 12, borderRadius: 10, marginBottom: 14, fontSize: 13, fontWeight: 500,
                background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
                color: message.type === 'ok' ? '#15803d' : '#b91c1c',
              }}>
                {message.text}
              </div>
            )}

            {/* Carteira de créditos */}
            <div style={{
              background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)',
              padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap'
            }}>
              <div style={{
                width: 46, height: 46, borderRadius: 12,
                background: 'linear-gradient(135deg,var(--nc-warning),#d97706)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0,
                boxShadow: '0 4px 10px rgba(217,119,6,.25)',
              }}>💳</div>
              <div style={{ flex: 1, minWidth: 80 }}>
                <div className="nc-hint">Saldo de créditos</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--nc-text)' }}>
                  {credits ? credits.balance : '—'}
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--nc-text-muted)', marginLeft: 4 }}>créditos</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/comprar-creditos')}
                style={{
                  padding: '9px 14px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg,var(--nc-boost),#5b21b6)',
                  color: 'var(--nc-on-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: 16 }}>💎</span> Comprar
              </button>
              {activeBoost && (
                <div style={{
                  fontSize: 10, fontWeight: 700, color: 'var(--nc-boost)', background: '#f5f3ff',
                  border: '1px solid #ddd6fe', padding: '6px 10px', borderRadius: 10, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 15 }}>⚡</div>
                  ATIVO
                </div>
              )}
            </div>

            {/* Boost ativo */}
            {activeBoost && (
              <div style={{
                background: 'linear-gradient(135deg,var(--nc-boost),#5b21b6)', color: 'var(--nc-on-primary)',
                borderRadius: 14, padding: 16, marginBottom: 16,
              }}>
                <div className="nc-row">
                  <span style={{ fontSize: 26 }}>⚡</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>
                      Boost {activeBoost.plan_name} em andamento
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
                      Expira em <strong>{remainingHours(activeBoost.expires_at)}h</strong> ({formatDateTime(activeBoost.expires_at)})
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Planos */}
            <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--nc-text)', margin: '0 0 4px' }}>Escolha um plano</h2>
            <p className="nc-hint" style={{ margin: '0 0 12px' }}>
              Enquanto ativo, seu comércio aparece primeiro na listagem da cidade.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {plans.map((plan, idx) => {
                const features = typeof plan.features === 'object' ? Object.keys(plan.features) : [];
                const canAfford = credits && credits.balance >= plan.credits_cost;
                const disabled = !!activeBoost || !canAfford || saving;
                const confirming = buying === plan.id;
                return (
                  <div key={plan.id} style={{
                    background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      background: PLAN_GRADIENT[idx % PLAN_GRADIENT.length], color: 'var(--nc-on-primary)',
                      padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                      <span style={{ fontSize: 22 }}>{['🚀', '🔥', '💎', '⭐', '👑', '🏆'][idx % 6]}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{plan.name}</div>
                        <div style={{ fontSize: 11, opacity: 0.9 }}>
                          {plan.duration_hours >= 24
                            ? (plan.duration_hours % 24 === 0 ? `${plan.duration_hours / 24} dia(s)` : `${plan.duration_hours}h`)
                            : `${plan.duration_hours}h`}
                        </div>
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800 }}>{plan.credits_cost}</div>
                    </div>
                    <div style={{ padding: 12 }}>
                      <div style={{ marginBottom: 10 }}>
                        {features.map((f) => <FeatureChip key={f} feature={f} />)}
                      </div>
                      {confirming ? (
                        <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: 10 }}>
                          <div style={{ fontSize: 13, color: '#4c1d95', marginBottom: 8, fontWeight: 500 }}>
                            Ativar {plan.name} por {plan.credits_cost} créditos?
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => handleBuy(plan)}
                              disabled={saving}
                              style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,var(--nc-boost),#5b21b6)', color: 'var(--nc-on-primary)', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}
                            >
                              {saving ? 'Ativando...' : 'Confirmar'}
                            </button>
                            <button
                              onClick={() => setBuying(null)}
                              disabled={saving}
                             className="nc-body-sm" style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid var(--nc-border)', background: 'var(--nc-surface)', cursor: 'pointer' }}>
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setBuying(plan.id)}
                          disabled={disabled}
                          title={activeBoost ? 'Já existe um boost ativo' : !canAfford ? 'Saldo insuficiente' : ''}
                          style={{
                            width: '100%', padding: '10px 0', borderRadius: 8, border: 'none',
                            background: disabled ? 'var(--nc-border)' : 'linear-gradient(135deg,#0d5c3b,#094a2f)',
                            color: disabled ? 'var(--nc-text-muted)' : '#fff',
                            fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
                            transition: 'transform .12s ease, opacity .12s ease',
                          }}
                          onMouseDown={disabled ? undefined : (e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                          onMouseUp={disabled ? undefined : (e) => { e.currentTarget.style.transform = ''; }}
                          onMouseLeave={disabled ? undefined : (e) => { e.currentTarget.style.transform = ''; }}
                        >
                          {activeBoost ? 'Impulsionamento ativo' : !canAfford ? 'Créditos insuficientes' : 'Impulsionar agora'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Extrato */}
            <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--nc-text)', margin: '20px 0 8px' }}>Extrato de créditos</h2>
            {credits && credits.transactions.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--nc-text-muted)', background: 'var(--nc-surface)', border: '1px solid var(--nc-border)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                Nenhum movimento ainda. Créditos aparecem aqui quando concedidos pela equipe.
              </div>
            ) : (
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', overflow: 'hidden' }}>
                {(credits?.transactions || []).map((t) => {
                  const meta = TYPE_LABEL[t.type] || TYPE_LABEL.refund;
                  const positive = t.amount >= 0;
                  return (
                    <div key={t.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                      borderBottom: '1px solid var(--nc-border)',
                    }}>
                      <span style={{
                        display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        color: meta.color, background: meta.bg, flexShrink: 0,
                      }}>
                        {meta.label}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--nc-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.description || meta.label}
                        </div>
                        <div className="nc-caption">{formatDate(t.created_at)}</div>
                      </div>
                      <div style={{
                        fontSize: 15, fontWeight: 700,
                        color: positive ? '#15803d' : '#b45309',
                      }}>
                        {positive ? '+' : ''}{t.amount}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
